import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { formatNum } from "../../util/index";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  FaShoppingCart,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaBoxOpen,
  FaTags,
  FaUsers,
  FaMoneyBillWave,
  FaPlusCircle,
  FaFileInvoiceDollar,
  FaPercentage,
  FaArrowRight
} from "react-icons/fa";

export default function Dashboard() {
  const [loading, setloading] = useState(true);
  const [dashboardDetails, setdashboardDetails] = useState({});

  useEffect(() => {
    document.title = "Admin Executive Dashboard — Gootocart";
    axios
      .get(`/api/view-dashboard-details`)
      .then((res) => {
        if (res.data.status === 200) {
          setdashboardDetails(res.data);
        }
        setloading(false);
      })
      .catch(() => {
        setloading(false);
      });
  }, []);

  const totalOrders = Number(dashboardDetails?.orderCount || 0);
  const completedOrders = Number(dashboardDetails?.completedOrderCount || 0);
  const pendingOrders = Number(dashboardDetails?.pendingOrdersCount || 0);
  const cancelOrders = Number(dashboardDetails?.cancelOrderCount || 0);

  // Chart 1: Order Status Breakdown
  const orderStatusOptions = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: 280,
      spacing: [8, 8, 8, 8],
      style: { fontFamily: "inherit" },
    },
    title: { text: null },
    xAxis: {
      categories: ["Completed", "Pending", "Cancelled"],
      lineColor: "#e2e8f0",
      tickColor: "#e2e8f0",
      labels: { style: { fontWeight: "600", color: "#64748b" } },
    },
    yAxis: {
      title: { text: "Orders", style: { color: "#64748b" } },
      gridLineColor: "#f1f5f9",
    },
    legend: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: 6,
        colorByPoint: true,
        colors: ["#16a34a", "#f59e0b", "#ef4444"],
        dataLabels: { enabled: true, style: { fontWeight: "700" } },
      },
    },
    series: [
      {
        name: "Orders",
        data: [completedOrders, pendingOrders, cancelOrders],
      },
    ],
    credits: { enabled: false },
  };

  // Chart 2: Order Fulfillment Ratio Pie
  const orderPieOptions = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 300,
      spacing: [8, 8, 8, 8],
      style: { fontFamily: "inherit" },
    },
    title: { text: null },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        innerSize: "55%",
        colors: ["#16a34a", "#f59e0b", "#ef4444"],
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f}%",
          style: { fontSize: "12px", color: "#334155" },
        },
      },
    },
    series: [
      {
        name: "Share",
        data: [
          { name: "Completed", y: completedOrders },
          { name: "Pending", y: pendingOrders },
          { name: "Cancelled", y: cancelOrders },
        ],
      },
    ],
    credits: { enabled: false },
  };

  // Chart 3: Sales Trend Graph
  const salesTrendOptions = {
    chart: {
      type: "area",
      backgroundColor: "transparent",
      height: 300,
      spacing: [8, 8, 8, 8],
      style: { fontFamily: "inherit" },
    },
    title: { text: null },
    xAxis: {
      categories:
        dashboardDetails?.salesGraphDate?.salesDates &&
        dashboardDetails.salesGraphDate.salesDates.length > 0
          ? dashboardDetails.salesGraphDate.salesDates
          : ["Recent Period"],
      lineColor: "#e2e8f0",
      tickColor: "#e2e8f0",
      labels: { style: { color: "#64748b" } },
    },
    yAxis: {
      title: { text: "Revenue (PKR)", style: { color: "#64748b" } },
      gridLineColor: "#f1f5f9",
    },
    plotOptions: {
      area: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(37, 99, 235, 0.35)"],
            [1, "rgba(37, 99, 235, 0.00)"],
          ],
        },
        marker: { radius: 4 },
        lineWidth: 3,
        lineColor: "#2563eb",
      },
    },
    series: [
      {
        name: "Order Revenue",
        data:
          dashboardDetails?.salesGraphDate?.salesCosts &&
          dashboardDetails.salesGraphDate.salesCosts.length > 0
            ? dashboardDetails.salesGraphDate.salesCosts
            : [0],
        color: "#2563eb",
      },
    ],
    credits: { enabled: false },
  };

  if (loading) {
    return (
      <div className="py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 fw-semibold">Loading Admin Dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      {/* 1. Top Executive KPI Cards */}
      <div className="row g-4 mb-4">
        {/* Card 1: Total Revenue */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Total Revenue</span>
              <div className="admin-stat-icon is-blue">
                <FaMoneyBillWave />
              </div>
            </div>
            <div className="admin-stat-value">
              Rs. {formatNum(dashboardDetails?.totalOrderComplete || dashboardDetails?.totalOrdersPrice || 0)}
            </div>
            <div className="admin-stat-footer text-success">
              <FaCheckCircle size={12} />
              <span>Fulfilled sales volume</span>
            </div>
          </div>
        </div>

        {/* Card 2: Total Orders */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Total Orders</span>
              <div className="admin-stat-icon is-ink">
                <FaShoppingCart />
              </div>
            </div>
            <div className="admin-stat-value">{totalOrders}</div>
            <div className="admin-stat-footer text-muted">
              <span>{completedOrders} completed • {pendingOrders} pending</span>
            </div>
          </div>
        </div>

        {/* Card 3: Active Products */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Catalog Products</span>
              <div className="admin-stat-icon is-green">
                <FaBoxOpen />
              </div>
            </div>
            <div className="admin-stat-value">{dashboardDetails?.productCount || 0}</div>
            <div className="admin-stat-footer text-muted">
              <span>Across {dashboardDetails?.categoryCount || 0} categories</span>
            </div>
          </div>
        </div>

        {/* Card 4: Registered Customers */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Total Customers</span>
              <div className="admin-stat-icon is-amber">
                <FaUsers />
              </div>
            </div>
            <div className="admin-stat-value">{dashboardDetails?.userCount || 0}</div>
            <div className="admin-stat-footer text-muted">
              <span>Registered & guest shoppers</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-card mb-4">
        <div className="admin-status-row">
          <div className="admin-status-item">
            <span className="admin-status-mark is-green"><FaCheckCircle size={16} /></span>
            <div>
              <span className="admin-status-label">Completed</span>
              <strong className="admin-status-value">{completedOrders}</strong>
            </div>
            <Link to="/admin/orders" className="admin-status-link">View</Link>
          </div>
          <div className="admin-status-item">
            <span className="admin-status-mark is-amber"><FaClock size={16} /></span>
            <div>
              <span className="admin-status-label">Pending</span>
              <strong className="admin-status-value">{pendingOrders}</strong>
            </div>
            <Link to="/admin/orders" className="admin-status-link">Process</Link>
          </div>
          <div className="admin-status-item">
            <span className="admin-status-mark is-red"><FaTimesCircle size={16} /></span>
            <div>
              <span className="admin-status-label">Cancelled</span>
              <strong className="admin-status-value">{cancelOrders}</strong>
            </div>
            <Link to="/admin/orders" className="admin-status-link">Inspect</Link>
          </div>
        </div>
      </div>

      {/* 3. Quick Action Shortcuts */}
      <div className="admin-card mb-4">
        <div className="admin-card-header">
          <h5 className="admin-card-title">Shortcuts</h5>
        </div>
        <div className="admin-card-body">
          <div className="row g-3">
            <div className="col-6 col-md-4 col-lg-2">
              <Link to="/admin/add-product" className="admin-quick-action-btn">
                <div className="admin-quick-action-icon"><FaPlusCircle /></div>
                <span className="fw-semibold" style={{ fontSize: "13px" }}>Add Product</span>
              </Link>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <Link to="/admin/view-product" className="admin-quick-action-btn">
                <div className="admin-quick-action-icon"><FaBoxOpen /></div>
                <span className="fw-semibold" style={{ fontSize: "13px" }}>Products</span>
              </Link>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <Link to="/admin/orders" className="admin-quick-action-btn">
                <div className="admin-quick-action-icon"><FaShoppingCart /></div>
                <span className="fw-semibold" style={{ fontSize: "13px" }}>All Orders</span>
              </Link>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <Link to="/admin/invoice" className="admin-quick-action-btn">
                <div className="admin-quick-action-icon"><FaFileInvoiceDollar /></div>
                <span className="fw-semibold" style={{ fontSize: "13px" }}>Show Sales</span>
              </Link>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <Link to="/admin/view-category" className="admin-quick-action-btn">
                <div className="admin-quick-action-icon"><FaTags /></div>
                <span className="fw-semibold" style={{ fontSize: "13px" }}>Categories</span>
              </Link>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <Link to="/admin/discount" className="admin-quick-action-btn">
                <div className="admin-quick-action-icon"><FaPercentage /></div>
                <span className="fw-semibold" style={{ fontSize: "13px" }}>Discounts</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Analytics & Charts */}
      <div className="row g-4 mb-4">
        {/* Sales Performance Area Graph */}
        <div className="col-12 col-xl-8">
          <div className="admin-card mb-0 h-100">
            <div className="admin-card-header">
              <h5 className="admin-card-title">Revenue & Sales Trends</h5>
            </div>
            <div className="admin-card-body">
              <HighchartsReact highcharts={Highcharts} options={salesTrendOptions} />
            </div>
          </div>
        </div>

        {/* Fulfillment Ratio Pie */}
        <div className="col-12 col-xl-4">
          <div className="admin-card mb-0 h-100">
            <div className="admin-card-header">
              <h5 className="admin-card-title">Order Status Ratio</h5>
            </div>
            <div className="admin-card-body">
              <HighchartsReact highcharts={Highcharts} options={orderPieOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Column Distribution Chart */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h5 className="admin-card-title">Order Processing Volume</h5>
        </div>
        <div className="admin-card-body">
          <HighchartsReact highcharts={Highcharts} options={orderStatusOptions} />
        </div>
      </div>
    </div>
  );
}
